import { useMemo, useState, type ReactNode } from "react";
import { readSessionUser } from "../../../lib/authSession";
import { exportRowsToCsv, type ExportColumn } from "../../../lib/excelExport";
import {
  removeIncomeExpenseEntry,
  upsertIncomeExpenseEntry,
  type IncomeExpenseEntry,
  type IncomeExpenseMode,
  type IncomeExpensePaymentMethod,
  type IncomeExpenseType,
} from "../../../lib/incomeExpenseStore";
import { setAllBankTransfers, type BankTransferRecord } from "../../../lib/bankTransferStore";
import { useIncomeExpense } from "../../../hooks/useIncomeExpense";
import { usePayments } from "../../../hooks/usePayments";
import { useCustomers } from "../../../hooks/useCustomers";
import { useBankTransfers } from "../../../hooks/useBankTransfers";
import { useAnimals } from "../../../hooks/useAnimals";
import { matchesOptionalDateRange } from "../../../lib/dateFilters";

const todayIso = new Date().toISOString().slice(0, 10);

type PeriodKey = "today" | "week" | "month" | "year" | "all" | "custom";
type SourceFilter = "Tümü" | "Tahsilat" | "Manuel Gelir" | "Manuel Gider";
type PaymentMethodFilter = "Tümü" | "Nakit" | "Havale";

function fmtMoney(value: number) {
  return value.toLocaleString("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 0,
  });
}

function parseAmount(value: string) {
  const normalized = String(value).replace(/\./g, "").replace(",", ".").replace(/[^\d.-]/g, "");
  return Number(normalized);
}

function formatAmountInput(value: string | number) {
  const parsed = typeof value === "number" ? value : parseAmount(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return "";
  return Math.round(parsed).toLocaleString("tr-TR");
}

function formatTransferDate(value: string) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("tr-TR");
}

function parseFinanceDate(value?: string) {
  const raw = String(value ?? "").trim();
  if (!raw) return null;

  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const date = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]), 12, 0, 0, 0);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const tr = raw.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})/);
  if (tr) {
    const date = new Date(Number(tr[3]), Number(tr[2]) - 1, Number(tr[1]), 12, 0, 0, 0);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const fallback = new Date(raw);
  if (Number.isNaN(fallback.getTime())) return null;
  fallback.setHours(12, 0, 0, 0);
  return fallback;
}

function getDateLimit(period: PeriodKey, customStart: string, customEnd: string) {
  const today = new Date();
  const end = period === "custom" && customEnd ? parseFinanceDate(customEnd) ?? today : today;
  const start = new Date(today);

  if (period === "today") {
    start.setHours(0, 0, 0, 0);
  } else if (period === "week") {
    start.setDate(today.getDate() - 7);
  } else if (period === "month") {
    start.setDate(1);
  } else if (period === "year") {
    start.setMonth(0, 1);
  } else if (period === "custom" && customStart) {
    return { start: parseFinanceDate(customStart) ?? new Date(customStart), end };
  } else {
    return null;
  }

  start.setHours(0, 0, 0, 0);
  return { start, end };
}

function isInPeriod(dateText: string, period: PeriodKey, customStart: string, customEnd: string) {
  const limit = getDateLimit(period, customStart, customEnd);
  if (!limit) return true;
  const value = parseFinanceDate(dateText);
  if (!value) return false;
  limit.start.setHours(0, 0, 0, 0);
  limit.end.setHours(23, 59, 59, 999);
  return value >= limit.start && value <= limit.end;
}

function Label({ children }: { children: ReactNode }) {
  return <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-farm-muted">{children}</label>;
}

function StatCard({
  label,
  value,
  tone = "brown",
  note,
}: {
  label: string;
  value: string | number;
  tone?: "brown" | "green" | "earth" | "red" | "gold" | "sky";
  note?: string;
}) {
  const cls = {
    brown: "text-farm-brown",
    green: "text-farm-green",
    earth: "text-farm-earth",
    red: "text-red-600",
    gold: "text-farm-gold",
    sky: "text-sky-700",
  }[tone];

  return (
    <div className="rounded-farm border border-farm-border bg-white px-3 py-2.5">
      <p className="text-[11px] text-farm-muted">{label}</p>
      <p className={`mt-0.5 text-sm font-bold ${cls}`}>{value}</p>
      {note && <p className="mt-0.5 text-[10px] text-farm-muted">{note}</p>}
    </div>
  );
}

function SectionTitle({ title, description }: { title: string; description?: string }) {
  return (
    <div className="mb-2">
      <h2 className="font-heading text-sm font-bold text-farm-brown">{title}</h2>
      {description && <p className="mt-0.5 text-[11px] text-farm-muted">{description}</p>}
    </div>
  );
}

function isTransferActive(item: BankTransferRecord) {
  return !item.automaticFilled && !item.manualFilled;
}

function getEntryTransferIds(item?: IncomeExpenseEntry | null) {
  return Array.isArray(item?.linkedTransferIds) ? item.linkedTransferIds.filter(Boolean) : [];
}

export default function GelirGiderPage() {
  const sessionUser = readSessionUser();
  const canAccess = sessionUser?.rol === "Admin" || sessionUser?.rol === "Muhasebe";

  const { entries } = useIncomeExpense();
  const { payments } = usePayments();
  const { customers } = useCustomers();
  const { transfers } = useBankTransfers();
  const { animals } = useAnimals();

  const [period, setPeriod] = useState<PeriodKey>("month");
  const [customStart, setCustomStart] = useState(todayIso);
  const [customEnd, setCustomEnd] = useState(todayIso);
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>("Tümü");
  const [methodFilter, setMethodFilter] = useState<PaymentMethodFilter>("Tümü");

  const [editingId, setEditingId] = useState<number | null>(null);
  const [tur, setTur] = useState<IncomeExpenseType>("Gider");
  const [odemeTipi, setOdemeTipi] = useState<IncomeExpensePaymentMethod>("Nakit");
  const [tekrarTipi, setTekrarTipi] = useState<IncomeExpenseMode>("Tek Sefer");
  const [tutar, setTutar] = useState("");
  const [kalemAdi, setKalemAdi] = useState("");
  const [tarih, setTarih] = useState(todayIso);
  const [aciklama, setAciklama] = useState("");
  const [kategori, setKategori] = useState("Genel");
  const [selectedTransferIds, setSelectedTransferIds] = useState<string[]>([]);
  const [transferPickerOpen, setTransferPickerOpen] = useState(false);
  const [transferDateFrom, setTransferDateFrom] = useState("");
  const [transferDateTo, setTransferDateTo] = useState("");
  const [transferDescriptionFilter, setTransferDescriptionFilter] = useState("");

  const periodPayments = useMemo(
    () => payments.filter((item) => isInPeriod(item.tarih, period, customStart, customEnd)),
    [payments, period, customStart, customEnd]
  );

  const periodEntries = useMemo(
    () => entries.filter((item) => isInPeriod(item.tarih, period, customStart, customEnd)),
    [entries, period, customStart, customEnd]
  );

  const selectedTransfers = useMemo(
    () => transfers.filter((item) => selectedTransferIds.includes(item.id)),
    [transfers, selectedTransferIds]
  );

  const selectedTransferFisNos = selectedTransfers.map((item) => item.fisNo).filter(Boolean);
  const selectedTransferTotal = selectedTransfers.reduce((sum, item) => sum + Number(item.tutar || 0), 0);

  const filteredTransferOptions = useMemo(() => {
    const descNeedle = transferDescriptionFilter.trim().toLocaleLowerCase("tr-TR");

    return transfers
      .filter((item) => isTransferActive(item) || selectedTransferIds.includes(item.id))
      .filter((item) => matchesOptionalDateRange(item.tarih, transferDateFrom, transferDateTo))
      .filter((item) => {
        if (!descNeedle) return true;
        const label = `${item.fisNo} ${item.aciklama} ${item.manualNote ?? ""}`.toLocaleLowerCase("tr-TR");
        return label.includes(descNeedle);
      })
      .sort((a, b) => b.tarih.localeCompare(a.tarih));
  }, [transfers, selectedTransferIds, transferDateFrom, transferDateTo, transferDescriptionFilter]);

  const cashPayments = periodPayments.filter((item) => item.tip === "Nakit").reduce((sum, item) => sum + Number(item.tutar || 0), 0);
  const bankPayments = periodPayments.filter((item) => item.tip === "Havale").reduce((sum, item) => sum + Number(item.tutar || 0), 0);
  const totalPayments = cashPayments + bankPayments;

  const cashManualIncome = periodEntries
    .filter((item) => item.tur === "Gelir" && item.odemeTipi === "Nakit")
    .reduce((sum, item) => sum + item.tutar, 0);
  const bankManualIncome = periodEntries
    .filter((item) => item.tur === "Gelir" && item.odemeTipi === "Havale")
    .reduce((sum, item) => sum + item.tutar, 0);
  const cashManualExpense = periodEntries
    .filter((item) => item.tur === "Gider" && item.odemeTipi === "Nakit")
    .reduce((sum, item) => sum + item.tutar, 0);
  const bankManualExpense = periodEntries
    .filter((item) => item.tur === "Gider" && item.odemeTipi === "Havale")
    .reduce((sum, item) => sum + item.tutar, 0);

  const manualIncome = cashManualIncome + bankManualIncome;
  const manualExpense = cashManualExpense + bankManualExpense;
  const totalCashIncome = cashPayments + cashManualIncome;
  const totalBankIncome = bankPayments + bankManualIncome;
  const totalCashExpense = cashManualExpense;
  const totalBankExpense = bankManualExpense;
  const netCash = totalCashIncome - totalCashExpense;
  const netBank = totalBankIncome - totalBankExpense;
  const totalIncome = totalCashIncome + totalBankIncome;
  const totalExpense = totalCashExpense + totalBankExpense;
  const netTotal = totalIncome - totalExpense;

  const totalDebt = customers.reduce((sum, item) => sum + Number(item.toplamBorc || 0), 0);
  const totalPaid = customers.reduce((sum, item) => sum + Number(item.toplamOdenen || 0), 0);
  const remainingReceivable = totalDebt - totalPaid;
  const activeTransfers = transfers.filter((item) => !item.automaticFilled && !item.manualFilled);
  const usedTransfers = transfers.filter((item) => item.automaticFilled || item.manualFilled);
  const soldAnimals = animals.filter((animal) => animal.hisseler.length > 0).length;

  const cashFlowRows = useMemo(() => {
    const autoRows = periodPayments.map((payment) => ({
      id: `payment-${payment.id}`,
      tarih: payment.tarih,
      kaynak: "Tahsilat" as const,
      odemeTipi: payment.tip,
      tip: payment.tip,
      cari: payment.musteriAdi,
      belge: payment.fisNo || payment.havaleNo || "",
      aciklama: payment.aciklama,
      gelir: payment.tutar,
      gider: 0,
      net: payment.tutar,
      entry: null as IncomeExpenseEntry | null,
    }));

    const manualRows = periodEntries.map((entry) => ({
      id: `entry-${entry.id}`,
      tarih: entry.tarih,
      kaynak: entry.tur === "Gelir" ? ("Manuel Gelir" as const) : ("Manuel Gider" as const),
      odemeTipi: entry.odemeTipi,
      tip: entry.tekrarTipi,
      cari: entry.kalemAdi,
      belge: entry.linkedTransferFisNos?.join(", ") ?? "",
      aciklama: entry.aciklama ?? "",
      gelir: entry.tur === "Gelir" ? entry.tutar : 0,
      gider: entry.tur === "Gider" ? entry.tutar : 0,
      net: entry.tur === "Gelir" ? entry.tutar : -entry.tutar,
      entry,
    }));

    return [...autoRows, ...manualRows]
      .filter((row) => sourceFilter === "Tümü" || row.kaynak === sourceFilter)
      .filter((row) => methodFilter === "Tümü" || row.odemeTipi === methodFilter)
      .sort((a, b) => b.tarih.localeCompare(a.tarih));
  }, [periodPayments, periodEntries, sourceFilter, methodFilter]);

  function resetForm() {
    setEditingId(null);
    setTur("Gider");
    setOdemeTipi("Nakit");
    setTekrarTipi("Tek Sefer");
    setTutar("");
    setKalemAdi("");
    setTarih(todayIso);
    setAciklama("");
    setKategori("Genel");
    setSelectedTransferIds([]);
    setTransferDateFrom("");
    setTransferDateTo("");
    setTransferDescriptionFilter("");
  }

  function splitCategoryAndName(value: string) {
    const [first, ...rest] = String(value || "").split(" / ");
    if (rest.length === 0) return { category: "Genel", name: value };
    return { category: first || "Genel", name: rest.join(" / ") };
  }

  function editItem(item: IncomeExpenseEntry) {
    const parsed = splitCategoryAndName(item.kalemAdi);
    setEditingId(item.id);
    setTur(item.tur);
    setOdemeTipi(item.odemeTipi || "Nakit");
    setTekrarTipi(item.tekrarTipi);
    setTutar(formatAmountInput(item.tutar));
    setKalemAdi(parsed.name);
    setTarih(item.tarih);
    setAciklama(item.aciklama ?? "");
    setKategori(parsed.category);
    setSelectedTransferIds(getEntryTransferIds(item));
  }

  function toggleTransferSelection(id: string) {
    setSelectedTransferIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  }

  function applySelectedTransferTotal() {
    if (selectedTransferTotal > 0) {
      setOdemeTipi("Havale");
      setTutar(formatAmountInput(selectedTransferTotal));
    }
    setTransferPickerOpen(false);
  }

  function removeSelectedTransfer(id: string) {
    setSelectedTransferIds((prev) => prev.filter((item) => item !== id));
  }

  async function syncLinkedTransfers(oldIds: string[], newIds: string[], entryLabel: string) {
    const oldSet = new Set(oldIds);
    const newSet = new Set(newIds);
    const now = new Date().toISOString();

    await setAllBankTransfers((prev) =>
      prev.map((item) => {
        if (newSet.has(item.id)) {
          return {
            ...item,
            automaticFilled: true,
            manualFilled: false,
            manualNote: "",
            updatedAt: now,
          };
        }

        if (oldSet.has(item.id) && !newSet.has(item.id)) {
          return {
            ...item,
            automaticFilled: false,
            manualFilled: false,
            manualNote: "",
            updatedAt: now,
          };
        }

        return item;
      })
    );
  }

  async function saveItem() {
    const amount = parseAmount(tutar);
    const oldEntry = editingId ? entries.find((item) => item.id === editingId) : null;
    const oldTransferIds = getEntryTransferIds(oldEntry);
    const newTransferIds = odemeTipi === "Havale" ? selectedTransferIds : [];
    const linkedFisNos = transfers
      .filter((item) => newTransferIds.includes(item.id))
      .map((item) => item.fisNo)
      .filter(Boolean);

    if (!kalemAdi.trim() || !tarih || !Number.isFinite(amount) || amount <= 0) {
      window.alert("Kalem adı, tarih ve geçerli tutar zorunludur.");
      return;
    }

    const fullLabel = `${kategori.trim() || "Genel"} / ${kalemAdi.trim()}`;

    await upsertIncomeExpenseEntry({
      id: editingId ?? undefined,
      tur,
      tekrarTipi,
      odemeTipi,
      tutar: Math.round(amount),
      kalemAdi: fullLabel,
      tarih,
      aciklama: aciklama.trim() || undefined,
      linkedTransferIds: newTransferIds,
      linkedTransferFisNos: linkedFisNos,
    });

    await syncLinkedTransfers(oldTransferIds, newTransferIds, `${tur} / ${fullLabel}`);
    resetForm();
  }

  async function deleteItem(item: IncomeExpenseEntry) {
    if (!window.confirm("Bu manuel kayıt silinsin mi? Bağlı havaleler tekrar aktif hale gelir.")) return;
    await removeIncomeExpenseEntry(item.id);
    await syncLinkedTransfers(getEntryTransferIds(item), [], "");
    if (editingId === item.id) resetForm();
  }

  function exportCurrent() {
    const columns: ExportColumn<(typeof cashFlowRows)[number]>[] = [
      { key: "tarih", label: "Tarih" },
      { key: "kaynak", label: "Kaynak" },
      { key: "odemeTipi", label: "Ödeme Şekli" },
      { key: "tip", label: "Tip" },
      { key: "cari", label: "Cari/Kalem" },
      { key: "belge", label: "Havale/Dekont" },
      { key: "aciklama", label: "Açıklama" },
      { key: "gelir", label: "Gelir" },
      { key: "gider", label: "Gider" },
      { key: "net", label: "Net" },
    ];
    exportRowsToCsv(`gelir-gider-akis-${new Date().toISOString().slice(0, 10)}`, cashFlowRows, columns);
  }

  if (!canAccess) {
    return (
      <div className="min-h-screen bg-farm-cream flex items-center justify-center px-4">
        <div className="bg-white border border-farm-border rounded-farm px-6 py-8 text-center max-w-sm w-full">
          <div className="w-12 h-12 flex items-center justify-center mx-auto rounded-full bg-red-50 text-red-500 mb-3">
            <i className="ri-lock-line text-2xl" />
          </div>
          <p className="font-heading text-base font-bold text-farm-brown">Yetkiniz yok</p>
          <p className="text-xs text-farm-muted mt-1">Bu sayfayı sadece Admin ve Muhasebe görebilir.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-farm-cream font-body">
      <div className="max-w-7xl mx-auto px-3 sm:px-5 py-4 space-y-4">
        <div className="rounded-farm border border-farm-border bg-white px-4 py-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h1 className="font-heading font-bold text-base text-farm-brown">Gelir / Gider</h1>
              <p className="text-xs text-farm-muted mt-1">
                Tahsilatları okuyan; manuel nakit/havale gelir-gider kayıtlarını ve günlük kasa durumunu gösteren finans merkezi.
              </p>
            </div>
            <button
              type="button"
              onClick={exportCurrent}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-farm-border bg-white text-farm-brown text-xs font-semibold hover:border-farm-earth/40"
            >
              <i className="ri-file-excel-2-line text-base" />
              Excel Çıktısı
            </button>
          </div>
        </div>

        <div className="rounded-farm border border-farm-border bg-white p-3">
          <SectionTitle title="Tahsilat Özeti" description="Ödeme Al / Ödemeler tarafından gelen hayvan tahsilatlarıdır." />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            <StatCard label="Nakit Tahsilat" value={fmtMoney(cashPayments)} tone="green" />
            <StatCard label="Havale Tahsilat" value={fmtMoney(bankPayments)} tone="sky" />
            <StatCard label="Toplam Tahsilat" value={fmtMoney(totalPayments)} tone="earth" />
          </div>
        </div>

        <div className="rounded-farm border border-farm-border bg-white p-3">
          <SectionTitle title="Manuel Gelir / Gider Özeti" description="Bu sayfadan girilen nakit/havale gelir ve gider kayıtlarıdır." />
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2">
            <StatCard label="Nakit Gelir" value={fmtMoney(cashManualIncome)} tone="green" />
            <StatCard label="Havale Gelir" value={fmtMoney(bankManualIncome)} tone="sky" />
            <StatCard label="Nakit Gider" value={fmtMoney(cashManualExpense)} tone="red" />
            <StatCard label="Havale Gider" value={fmtMoney(bankManualExpense)} tone="red" />
            <StatCard label="Toplam Gelir" value={fmtMoney(manualIncome)} tone="green" />
            <StatCard label="Toplam Gider" value={fmtMoney(manualExpense)} tone="red" />
          </div>
        </div>

        <div className="rounded-farm border border-farm-border bg-white p-3">
          <SectionTitle title="Günlük Kasa / Genel Net" description="Tahsilatlar ile manuel gelir-giderlerin birleşik nakit ve havale dengesi." />
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2">
            <StatCard label="Toplam Nakit Gelir" value={fmtMoney(totalCashIncome)} tone="green" note="Nakit tahsilat + nakit gelir" />
            <StatCard label="Toplam Havale Gelir" value={fmtMoney(totalBankIncome)} tone="sky" note="Havale tahsilat + havale gelir" />
            <StatCard label="Toplam Nakit Gider" value={fmtMoney(totalCashExpense)} tone="red" />
            <StatCard label="Toplam Havale Gider" value={fmtMoney(totalBankExpense)} tone="red" />
            <StatCard label="Net Nakit" value={fmtMoney(netCash)} tone={netCash >= 0 ? "green" : "red"} />
            <StatCard label="Net Havale" value={fmtMoney(netBank)} tone={netBank >= 0 ? "sky" : "red"} />
            <StatCard label="Kalan Alacaklar Toplamı" value={fmtMoney(remainingReceivable)} tone={remainingReceivable >= 0 ? "earth" : "green"} />
          </div>
          <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-2">
            <StatCard label="Toplam Gelir" value={fmtMoney(totalIncome)} tone="green" />
            <StatCard label="Toplam Gider" value={fmtMoney(totalExpense)} tone="red" />
            <StatCard label="Genel Net" value={fmtMoney(netTotal)} tone={netTotal >= 0 ? "green" : "red"} />
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-[380px_minmax(0,1fr)]">
          <div className="space-y-4">
            <div className="rounded-farm border border-farm-border bg-white p-4">
              <h2 className="font-heading text-sm font-bold text-farm-brown">Manuel Gelir / Gider</h2>
              <p className="mt-1 text-[11px] text-farm-muted">Nakit veya havale olarak manuel gelir/gider ekle. Havale seçersen aktif havale bağlayabilirsin.</p>
              <div className="mt-3 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  {(["Gelir", "Gider"] as const).map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => setTur(item)}
                      className={`h-9 rounded-xl border text-xs font-semibold ${
                        tur === item
                          ? item === "Gelir"
                            ? "border-farm-green bg-farm-green text-white"
                            : "border-farm-earth bg-farm-earth text-white"
                          : "border-farm-border bg-white text-farm-brown"
                      }`}
                    >
                      {item}
                    </button>
                  ))}
                </div>

                <div className="grid grid-cols-2 gap-2">
                  {(["Nakit", "Havale"] as const).map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => setOdemeTipi(item)}
                      className={`h-9 rounded-xl border text-xs font-semibold ${
                        odemeTipi === item
                          ? item === "Nakit"
                            ? "border-farm-green bg-farm-green text-white"
                            : "border-sky-600 bg-sky-600 text-white"
                          : "border-farm-border bg-white text-farm-brown"
                      }`}
                    >
                      {item}
                    </button>
                  ))}
                </div>

                <div>
                  <Label>Tarih</Label>
                  <input type="date" value={tarih} onChange={(e) => setTarih(e.target.value)} className="h-10 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm outline-none" />
                </div>
                <div>
                  <Label>Kategori</Label>
                  <select value={kategori} onChange={(e) => setKategori(e.target.value)} className="h-10 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm outline-none">
                    {["Genel", "Yem", "Nakliye", "Kesim İşçilik", "Personel", "Elektrik/Su", "Veteriner", "Mazot", "Et Satışı", "Hizmet Geliri", "Kasap Satışı", "Diğer"].map((item) => (
                      <option key={item}>{item}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Kalem / Cari</Label>
                  <input value={kalemAdi} onChange={(e) => setKalemAdi(e.target.value)} placeholder="Örn: Yem alımı, kasap satışı" className="h-10 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm outline-none" />
                </div>
                <div>
                  <Label>Tutar</Label>
                  <input
                    value={tutar}
                    onChange={(e) => setTutar(formatAmountInput(e.target.value))}
                    onBlur={(e) => setTutar(formatAmountInput(e.target.value))}
                    placeholder="0"
                    className="h-10 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm outline-none"
                  />
                </div>

                {odemeTipi === "Havale" && (
                  <div className="rounded-xl border border-sky-100 bg-sky-50/50 p-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <Label>Havale Bağla</Label>
                        <p className="text-[10px] text-farm-muted">Gelir veya gider kaydına aktif havale bağlayabilirsin.</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setTransferPickerOpen(true)}
                        className="rounded-full bg-sky-600 px-3 py-1.5 text-[10px] font-semibold text-white hover:bg-sky-700"
                      >
                        Havale Seç
                      </button>
                    </div>
                    {selectedTransfers.length > 0 && (
                      <div className="mt-2 space-y-1">
                        <div className="text-[10px] font-semibold text-sky-700">Seçili: {fmtMoney(selectedTransferTotal)}</div>
                        <div className="flex flex-wrap gap-1.5">
                          {selectedTransfers.map((item) => (
                            <button
                              key={item.id}
                              type="button"
                              onClick={() => removeSelectedTransfer(item.id)}
                              className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-white px-2 py-1 text-[10px] font-semibold text-sky-700 hover:bg-red-50 hover:text-red-600"
                            >
                              {item.fisNo}
                              <i className="ri-close-line" />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <Label>Tekrar Tipi</Label>
                  <select value={tekrarTipi} onChange={(e) => setTekrarTipi(e.target.value as IncomeExpenseMode)} className="h-10 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm outline-none">
                    <option>Tek Sefer</option>
                    <option>Sabit</option>
                  </select>
                </div>
                <div>
                  <Label>Açıklama</Label>
                  <textarea value={aciklama} onChange={(e) => setAciklama(e.target.value)} rows={3} className="w-full rounded-xl border border-farm-border bg-farm-cream px-3 py-2 text-sm outline-none" />
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={saveItem} className="h-10 flex-1 rounded-xl bg-farm-earth text-xs font-semibold text-white">
                    {editingId ? "Güncelle" : "Kaydet"}
                  </button>
                  {editingId && (
                    <button type="button" onClick={resetForm} className="h-10 rounded-xl border border-farm-border bg-white px-3 text-xs font-semibold text-farm-muted">
                      Vazgeç
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="rounded-farm border border-farm-border bg-white p-4">
              <h2 className="font-heading text-sm font-bold text-farm-brown">Cari / Havale Özeti</h2>
              <div className="mt-3 space-y-2 text-xs">
                <div className="flex justify-between"><span className="text-farm-muted">Müşteri Borcu</span><strong>{fmtMoney(totalDebt)}</strong></div>
                <div className="flex justify-between"><span className="text-farm-muted">Müşteri Ödenen</span><strong>{fmtMoney(totalPaid)}</strong></div>
                <div className="flex justify-between"><span className="text-farm-muted">Aktif Havale</span><strong>{activeTransfers.length} / {fmtMoney(activeTransfers.reduce((s, x) => s + x.tutar, 0))}</strong></div>
                <div className="flex justify-between"><span className="text-farm-muted">Bağlı/Pasif Havale</span><strong>{usedTransfers.length} / {fmtMoney(usedTransfers.reduce((s, x) => s + x.tutar, 0))}</strong></div>
                <div className="flex justify-between"><span className="text-farm-muted">Satış Görmüş Hayvan</span><strong>{soldAnimals}</strong></div>
              </div>
            </div>
          </div>

          <div className="rounded-farm border border-farm-border bg-white p-4">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2 className="font-heading text-sm font-bold text-farm-brown">Finans Akışı</h2>
                <p className="text-xs text-farm-muted">Ödeme kayıtları sadece okunur. Manuel kayıtlar bu sayfanın kendi kayıtlarıdır.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <select value={period} onChange={(e) => setPeriod(e.target.value as PeriodKey)} className="h-9 rounded-xl border border-farm-border bg-farm-cream px-2 text-xs outline-none">
                  <option value="today">Bugün</option>
                  <option value="week">Son 7 Gün</option>
                  <option value="month">Bu Ay</option>
                  <option value="year">Bu Yıl</option>
                  <option value="all">Tüm Zamanlar</option>
                  <option value="custom">Özel Tarih</option>
                </select>
                <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value as SourceFilter)} className="h-9 rounded-xl border border-farm-border bg-farm-cream px-2 text-xs outline-none">
                  <option>Tümü</option>
                  <option>Tahsilat</option>
                  <option>Manuel Gelir</option>
                  <option>Manuel Gider</option>
                </select>
                <select value={methodFilter} onChange={(e) => setMethodFilter(e.target.value as PaymentMethodFilter)} className="h-9 rounded-xl border border-farm-border bg-farm-cream px-2 text-xs outline-none">
                  <option>Tümü</option>
                  <option>Nakit</option>
                  <option>Havale</option>
                </select>
              </div>
            </div>
            {period === "custom" && (
              <div className="mt-3 flex gap-2">
                <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="h-9 rounded-xl border border-farm-border bg-farm-cream px-2 text-xs" />
                <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="h-9 rounded-xl border border-farm-border bg-farm-cream px-2 text-xs" />
              </div>
            )}

            <div className="mt-3 overflow-hidden rounded-2xl border border-farm-border">
              <div className="hidden md:grid grid-cols-[90px_95px_80px_80px_1fr_105px_105px_95px] bg-farm-cream px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-farm-muted">
                <div>Tarih</div><div>Kaynak</div><div>Ödeme</div><div>Tip</div><div>Cari / Açıklama</div><div className="text-right">Gelir</div><div className="text-right">Gider</div><div className="text-right">Net</div>
              </div>
              {cashFlowRows.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-farm-muted">Bu dönemde kayıt yok.</div>
              ) : (
                cashFlowRows.map((row) => (
                  <div key={row.id} className="border-t border-farm-border px-3 py-2 text-xs md:grid md:grid-cols-[90px_95px_80px_80px_1fr_105px_105px_95px] md:items-center">
                    <div>{row.tarih}</div>
                    <div className="font-semibold text-farm-brown">{row.kaynak}</div>
                    <div className={row.odemeTipi === "Havale" ? "font-semibold text-sky-700" : "font-semibold text-farm-green"}>{row.odemeTipi}</div>
                    <div>{row.tip}</div>
                    <div className="min-w-0">
                      <div className="font-semibold text-farm-brown truncate">{row.cari}</div>
                      <div className="text-[11px] text-farm-muted truncate">{row.belge ? `${row.belge} • ` : ""}{row.aciklama || "—"}</div>
                    </div>
                    <div className="text-right text-farm-green">{row.gelir ? fmtMoney(row.gelir) : "—"}</div>
                    <div className="text-right text-red-600">{row.gider ? fmtMoney(row.gider) : "—"}</div>
                    <div className={`text-right font-bold ${row.net >= 0 ? "text-farm-green" : "text-red-600"}`}>{fmtMoney(row.net)}</div>
                    {row.entry && (
                      <div className="mt-2 flex gap-2 md:col-span-8">
                        <button type="button" onClick={() => editItem(row.entry!)} className="text-[11px] font-semibold text-farm-earth">Düzenle</button>
                        <button type="button" onClick={() => deleteItem(row.entry!)} className="text-[11px] font-semibold text-red-600">Sil</button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {transferPickerOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 px-3">
          <div className="w-full max-w-4xl rounded-xl bg-white p-4 shadow-xl">
            <div className="flex items-start justify-between gap-3 border-b border-gray-100 pb-3">
              <div>
                <h4 className="text-sm font-bold text-gray-800">Aktif Havale Bağla</h4>
                <p className="text-[10px] text-gray-500">Boşta duran aktif havaleleri manuel gelir/gider kaydına bağlar. Birden fazla kutucuğu seçebilirsin.</p>
              </div>
              <button type="button" onClick={() => setTransferPickerOpen(false)} className="h-8 w-8 rounded-full text-gray-400 hover:bg-gray-100">
                <i className="ri-close-line" />
              </button>
            </div>
            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
              <input
                type="date"
                value={transferDateFrom}
                onChange={(event) => setTransferDateFrom(event.target.value)}
                className="h-10 rounded-lg border border-gray-200 px-3 text-xs outline-none focus:border-sky-400"
                title="Başlangıç tarihi"
              />
              <input
                type="date"
                value={transferDateTo}
                onChange={(event) => setTransferDateTo(event.target.value)}
                className="h-10 rounded-lg border border-gray-200 px-3 text-xs outline-none focus:border-sky-400"
                title="Bitiş tarihi"
              />
              <input
                value={transferDescriptionFilter}
                onChange={(event) => setTransferDescriptionFilter(event.target.value)}
                placeholder="Açıklama / Fiş No içinde ara"
                className="h-10 rounded-lg border border-gray-200 px-3 text-xs outline-none focus:border-sky-400"
              />
            </div>
            <div className="mt-3 max-h-[360px] divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-100">
              {filteredTransferOptions.length === 0 ? (
                <div className="px-4 py-8 text-center text-xs text-gray-500">Aktif havale yok.</div>
              ) : (
                filteredTransferOptions.map((item) => {
                  const checked = selectedTransferIds.includes(item.id);
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => toggleTransferSelection(item.id)}
                      className={`grid w-full grid-cols-[28px_86px_86px_minmax(0,1fr)_100px] items-start gap-2 px-3 py-2.5 text-left text-xs hover:bg-sky-50 ${checked ? "bg-sky-50" : ""}`}
                    >
                      <span className="pt-0.5">
                        <span className={`flex h-4 w-4 items-center justify-center rounded border ${checked ? "border-sky-600 bg-sky-600 text-white" : "border-gray-300 bg-white"}`}>
                          {checked && <i className="ri-check-line text-xs" />}
                        </span>
                      </span>
                      <span className="font-semibold text-gray-500">{formatTransferDate(item.tarih)}</span>
                      <span className="font-bold text-sky-700">{item.fisNo}</span>
                      <span className="whitespace-normal break-words leading-snug text-gray-700">{item.aciklama || "—"}</span>
                      <span className="text-right font-bold text-gray-800">{fmtMoney(item.tutar)}</span>
                    </button>
                  );
                })
              )}
            </div>
            <div className="mt-3 flex items-center justify-between gap-3 border-t border-gray-100 pt-3">
              <p className="text-[10px] text-gray-500">
                {selectedTransferIds.length} havale seçildi. Toplam: <strong>{fmtMoney(selectedTransferTotal)}</strong>. Açıklama tam görünür; kısaltılmaz.
              </p>
              <button
                type="button"
                onClick={applySelectedTransferTotal}
                disabled={selectedTransferIds.length === 0}
                className="rounded-full bg-sky-600 px-4 py-2 text-xs font-semibold text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Seçili Havaleleri Bağla
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
